"use client"

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CompanyIcon, BusinessIcon, PartnerIcon } from '../../icons'
import { Monitor, RefreshCw, BookOpen, Star } from 'lucide-react'

interface EventsStepProps {
  uid: string
  onNext: () => void
  onBack: () => void
}

export default function EventsStep({ uid, onNext, onBack }: EventsStepProps) {
  const { t } = useTranslation();
  
  const [eventPrefs, setEventPrefs] = useState({
    mode: 'online' as 'online' | 'hybride' | 'fysiek',
    regions: [] as string[],
    themes: [] as string[],
    speakerIntent: false,
    hostIntent: false
  })

  const [contentPrefs, setContentPrefs] = useState({
    topics: [] as string[],
    podcastExp: 'beginner' as 'beginner' | 'intermediate' | 'expert'
  })

  const [matchPrefs, setMatchPrefs] = useState({
    prefer: 'clients' as 'clients' | 'partners' | 'mentors' | 'all',
    ambition: 'groeiend' as 'startend' | 'groeiend' | 'gevestigd',
    rewards: [] as string[]
  })

  // Auto-save when data changes
  const saveData = async () => {
    try {
      const { doc, setDoc } = await import('firebase/firestore')
      const { db } = await import('../../../firebase')
      
      await setDoc(doc(db, 'userProfiles', uid), {
        eventPrefs,
        contentPrefs,
        matchPrefs,
        updatedAt: Date.now()
      }, { merge: true })
    } catch (error) {
      console.error('Error saving event preferences:', error)
    }
  }

  // Auto-save on data changes
  React.useEffect(() => {
    if (uid) {
      saveData()
    }
  }, [eventPrefs, contentPrefs, matchPrefs, uid])

  const toggleArrayItem = (array: string[], item: string) => {
    return array.includes(item) 
      ? array.filter(i => i !== item)
      : [...array, item]
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('events.title')}
        </h2>
        <p className="text-gray-600">
          {t('events.subtitle')}
        </p>
      </div>

      {/* Event Mode */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('events.preferences')}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.mode')}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'online', label: t('events.online'), icon: <Monitor size={20} className="text-gray-600" /> },
                { value: 'hybride', label: t('events.hybrid'), icon: <RefreshCw size={20} className="text-gray-600" /> },
                { value: 'fysiek', label: t('events.physical'), icon: <CompanyIcon size={20} weight="regular" className="text-gray-600" /> }
              ].map((option) => (
                <label key={option.value} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="eventMode"
                    value={option.value}
                    checked={eventPrefs.mode === option.value}
                    onChange={(e) => setEventPrefs(prev => ({ ...prev, mode: e.target.value as any }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span>{option.icon}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.regions')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {['Nederland', 'België', 'Duitsland', 'Verenigd Koninkrijk', 'Europa', 'Wereldwijd'].map((region) => (
                <label key={region} className="flex items-center space-x-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={eventPrefs.regions.includes(region)}
                    onChange={() => setEventPrefs(prev => ({ 
                      ...prev, 
                      regions: toggleArrayItem(prev.regions, region) 
                    }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{region}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.themes')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                'Tech & Innovatie', 'Marketing & Sales', 'Finance & Funding', 
                'Leadership & Management', 'Networking', 'Personal Development',
                'Sustainability', 'Health & Wellness', 'Education'
              ].map((theme) => (
                <label key={theme} className="flex items-center space-x-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={eventPrefs.themes.includes(theme)}
                    onChange={() => setEventPrefs(prev => ({ 
                      ...prev, 
                      themes: toggleArrayItem(prev.themes, theme) 
                    }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{theme}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={eventPrefs.speakerIntent}
                onChange={(e) => setEventPrefs(prev => ({ ...prev, speakerIntent: e.target.checked }))}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Ik wil graag spreken op events</span>
            </label>
            
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={eventPrefs.hostIntent}
                onChange={(e) => setEventPrefs(prev => ({ ...prev, hostIntent: e.target.checked }))}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Ik wil graag events hosten</span>
            </label>
          </div>
        </div>
      </div>

      {/* Content Preferences */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('events.content')}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.podcastExperience')}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'beginner', label: t('events.beginner'), desc: 'Nog nooit gedaan' },
                { value: 'intermediate', label: t('events.experienced'), desc: 'Enkele keren' },
                { value: 'expert', label: t('events.expert'), desc: 'Regelmatig' }
              ].map((option) => (
                <label key={option.value} className="flex flex-col p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="podcastExp"
                    value={option.value}
                    checked={contentPrefs.podcastExp === option.value}
                    onChange={(e) => setContentPrefs(prev => ({ ...prev, podcastExp: e.target.value as any }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div className="mt-1">
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interessegebieden (meerdere keuzes mogelijk)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                'Business Strategy', 'Digital Marketing', 'Sales & Growth', 
                'Product Development', 'Team Building', 'Financial Planning',
                'Innovation', 'Sustainability', 'Personal Branding'
              ].map((topic) => (
                <label key={topic} className="flex items-center space-x-2 p-2 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={contentPrefs.topics.includes(topic)}
                    onChange={() => setContentPrefs(prev => ({ 
                      ...prev, 
                      topics: toggleArrayItem(prev.topics, topic) 
                    }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{topic}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Match Preferences */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('events.match')}</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.seeking')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 'clients', label: t('events.clients'), icon: <BusinessIcon size={20} weight="regular" className="text-gray-600" /> },
                { value: 'partners', label: t('events.partners'), icon: <PartnerIcon size={20} weight="regular" className="text-gray-600" /> },
                { value: 'mentors', label: t('events.mentors'), icon: <BookOpen size={20} className="text-gray-600" /> },
                { value: 'all', label: t('events.all'), icon: <Star size={20} className="text-gray-600" /> }
              ].map((option) => (
                <label key={option.value} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="matchPrefer"
                    value={option.value}
                    checked={matchPrefs.prefer === option.value}
                    onChange={(e) => setMatchPrefs(prev => ({ ...prev, prefer: e.target.value as any }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span>{option.icon}</span>
                  <span className="text-sm font-medium">{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('events.ambition')}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'startend', label: t('events.starting'), desc: 'Net begonnen' },
                { value: 'groeiend', label: t('events.growing'), desc: 'In ontwikkeling' },
                { value: 'gevestigd', label: t('events.established'), desc: 'Welnig ervaring' }
              ].map((option) => (
                <label key={option.value} className="flex flex-col p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="ambition"
                    value={option.value}
                    checked={matchPrefs.ambition === option.value}
                    onChange={(e) => setMatchPrefs(prev => ({ ...prev, ambition: e.target.value as any }))}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <div className="mt-1">
                    <div className="text-sm font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Note: Navigation handled by parent OnboardingWizard */}
    </div>
  )
}

